import React, { useMemo, useState } from 'react';
import { AlertTriangle, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useFacilityData } from '../hooks/booking/useBookingContextSlices';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { clearAllData } = useFacilityData();
  const { isSuperAdmin } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dangerTitleId = useMemo(() => `danger-${Math.random().toString(16).slice(2)}`, []);
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const isDev = process.env.NODE_ENV !== 'production';

  const handleConfirmClearAll = () => {
    if (!isSuperAdmin) return;
    setConfirmOpen(false);
    clearAllData();
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="ga-card rounded-lg shadow-sm border border-ga-border-subtle/60">
          <h1 className="text-2xl font-bold text-ga-text-primary">Settings</h1>
          <p className="text-ga-text-muted mt-1 text-sm">
            Manage application preferences and platform configuration.
          </p>
        </div>

        {isSuperAdmin && isDev && (
          <>
            {/* Developer Tools — hidden in production */}
            <div className="ga-card rounded-lg shadow-sm border border-ga-border-subtle/40">
              <button
                type="button"
                onClick={() => setDevToolsOpen(o => !o)}
                className="flex w-full items-center justify-between text-sm font-medium text-ga-text-muted hover:text-ga-text-primary"
              >
                <span>Developer Tools</span>
                {devToolsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
            {devToolsOpen && (
            <div className="ga-card rounded-lg shadow-sm border border-ga-danger/30 bg-ga-bg-surface-subtle/40">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-ga-md bg-ga-danger/10 border border-ga-danger/30 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-ga-danger" />
                </div>

                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-ga-text-primary">Danger Zone</h2>
                  <p className="text-xs text-ga-text-muted mt-1 max-w-2xl">
                    Clearing data affects only this browser (local storage). Server data is not deleted.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-ga-danger/50 text-ga-danger rounded-ga-sm hover:bg-ga-danger/15 transition-colors text-sm font-medium min-h-10"
                >
                  <Trash2 className="h-4 w-4" />
                  Clear All Data
                </button>
              </div>
            </div>
            )}

            {confirmOpen && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div
                  className="w-full max-w-xl rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft p-5"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={dangerTitleId}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-ga-md bg-ga-danger/10 border border-ga-danger/30 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-ga-danger" />
                    </div>
                    <div className="min-w-0">
                      <h2 id={dangerTitleId} className="text-base font-bold text-ga-text-primary">
                        Confirm Clear All Data
                      </h2>
                      <p className="text-sm text-ga-text-muted mt-1">
                        This removes bookings, facility configuration, and slot data stored in this browser only.
                        <span className="block mt-1 text-ga-text-subtle">This action cannot be undone.</span>
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmOpen(false)}
                      className="px-4 py-2 rounded-ga-md border border-ga-border-subtle text-ga-text-primary hover:bg-white/5 min-h-10"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmClearAll}
                      className="px-4 py-2 rounded-ga-md bg-ga-danger text-white hover:opacity-95 min-h-10"
                    >
                      Yes, Clear All Data
                    </button>
                  </div>
                </div>
              </div>
            )}
            </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Settings;

